import { useMemo, useState } from 'react'

import { useElementWidth } from '../shared/useElementWidth'

import { weekLabel, weekShort } from './format'

const HEIGHT = 280
const PAD = { top: 18, right: 18, bottom: 40, left: 42 }
const COLORS = ['#0f5b99', '#ef4444', '#facc15', '#4d9c1a', '#8b5cf6', '#06b6d4']
const OTHER_KEY = '__other__'

export interface FrozenStackRow {
  week: string
  itemKey: string
  itemName: string
  shipments: number
}

interface Segment {
  itemKey: string
  itemName: string
  value: number
}

interface WeekStack {
  week: string
  total: number
  segments: Segment[]
}

interface Props {
  rows: readonly FrozenStackRow[]
  selectedWeek: string | null
  /**
   * Omitted by the over-temperature panel, which buckets weeks on the tanker
   * receiving date rather than the COA creation date the selectable panels
   * share. Letting it drive the week parameter would point the detail tables
   * at a week boundary they do not use.
   */
  onSelectWeek?: (week: string) => void
  noun: string
  emptyLabel: string
}

function ticksFor(max: number): number[] {
  const raw = Math.max(1, max) / 4
  const magnitude = Math.pow(10, Math.floor(Math.log10(raw)))
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let value = 0; value <= max + step * 0.001; value += step) ticks.push(value)
  return ticks
}

export function FrozenTrendChart({ rows, selectedWeek, onSelectWeek, noun, emptyLabel }: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const [hovered, setHovered] = useState<WeekStack | null>(null)

  const { stacks, itemKeys, itemNames } = useMemo(() => {
    const byWeek = new Map<string, Map<string, Segment>>()
    const names = new Map<string, string>()
    const totalsByItem = new Map<string, number>()
    for (const row of rows) {
      names.set(row.itemKey, row.itemName)
      totalsByItem.set(row.itemKey, (totalsByItem.get(row.itemKey) ?? 0) + row.shipments)
      const week = byWeek.get(row.week) ?? new Map<string, Segment>()
      byWeek.set(row.week, week)
      week.set(row.itemKey, {
        itemKey: row.itemKey,
        itemName: row.itemName,
        value: (week.get(row.itemKey)?.value ?? 0) + row.shipments,
      })
    }
    const topItemKeys = [...totalsByItem.entries()]
      .sort(([aKey, a], [bKey, b]) => b - a || aKey.localeCompare(bKey))
      .slice(0, COLORS.length - 1)
      .map(([key]) => key)
    const hasOther = [...names.keys()].some((key) => !topItemKeys.includes(key))
    const keys = hasOther ? [...topItemKeys, OTHER_KEY] : topItemKeys
    if (hasOther) names.set(OTHER_KEY, 'Other')
    const built = [...byWeek.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([week, weekSegments]) => {
        const segments = topItemKeys
          .map(
            (key) =>
              weekSegments.get(key) ?? { itemKey: key, itemName: names.get(key) ?? key, value: 0 },
          )
          .filter((segment) => segment.value > 0)
        const otherValue = [...weekSegments.entries()]
          .filter(([key]) => !topItemKeys.includes(key))
          .reduce((sum, [, segment]) => sum + segment.value, 0)
        if (otherValue > 0) {
          segments.push({ itemKey: OTHER_KEY, itemName: 'Other', value: otherValue })
        }
        return {
          week,
          segments,
          total: segments.reduce((sum, segment) => sum + segment.value, 0),
        }
      })
    return { stacks: built, itemKeys: keys, itemNames: names }
  }, [rows])

  if (stacks.length === 0) return <div className="state">{emptyLabel}</div>

  const chartWidth = Math.max(width, 360)
  const plotWidth = chartWidth - PAD.left - PAD.right
  const plotHeight = HEIGHT - PAD.top - PAD.bottom
  const ticks = ticksFor(Math.max(...stacks.map((stack) => stack.total)))
  const scaleMax = ticks[ticks.length - 1] ?? 1
  const band = plotWidth / stacks.length
  const barWidth = Math.min(42, Math.max(12, band - 12))
  const yOf = (value: number) => PAD.top + plotHeight - (value / scaleMax) * plotHeight
  const labelEvery = Math.ceil((stacks.length * 42) / Math.max(plotWidth, 1))

  return (
    <div>
      <div className="chart-wrap" ref={ref}>
        <svg width={chartWidth} height={HEIGHT} role="img" aria-label={`${noun} by week`}>
          {ticks.map((tick) => (
            <g key={tick}>
              <line
                x1={PAD.left}
                x2={chartWidth - PAD.right}
                y1={yOf(tick)}
                y2={yOf(tick)}
                stroke={tick === 0 ? 'var(--baseline)' : 'var(--gridline)'}
              />
              <text
                x={PAD.left - 8}
                y={yOf(tick) + 4}
                textAnchor="end"
                fontSize={11}
                fill="var(--text-muted)"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {tick}
              </text>
            </g>
          ))}

          {stacks.map((stack, index) => {
            const x = PAD.left + band * index + (band - barWidth) / 2
            let running = 0
            const isSelected = stack.week === selectedWeek
            return (
              <g key={stack.week}>
                {stack.segments.map((segment) => {
                  const y = yOf(running + segment.value)
                  const h = yOf(running) - y
                  running += segment.value
                  const colorIndex = Math.max(0, itemKeys.indexOf(segment.itemKey))
                  return (
                    <rect
                      key={segment.itemKey}
                      x={x}
                      y={y}
                      width={barWidth}
                      height={Math.max(0, h)}
                      fill={COLORS[colorIndex % COLORS.length]}
                      opacity={isSelected ? 1 : 0.78}
                    />
                  )
                })}
                <rect
                  x={PAD.left + band * index}
                  y={PAD.top}
                  width={band}
                  height={plotHeight}
                  fill="transparent"
                  className="bar-rect"
                  style={onSelectWeek === undefined ? undefined : { cursor: 'pointer' }}
                  onMouseEnter={() => {
                    setHovered(stack)
                  }}
                  onMouseLeave={() => {
                    setHovered(null)
                  }}
                  onClick={() => {
                    onSelectWeek?.(stack.week)
                  }}
                />
                {isSelected && (
                  <text
                    x={x + barWidth / 2}
                    y={yOf(stack.total) - 7}
                    textAnchor="middle"
                    fontSize={12}
                    fontWeight={600}
                    fill="var(--text-primary)"
                  >
                    {stack.total}
                  </text>
                )}
                {(index % labelEvery === 0 || isSelected) && (
                  <text
                    x={PAD.left + band * index + band / 2}
                    y={HEIGHT - PAD.bottom + 16}
                    textAnchor="middle"
                    fontSize={11}
                    fill={isSelected ? 'var(--text-primary)' : 'var(--text-muted)'}
                  >
                    {weekShort(stack.week)}
                  </text>
                )}
              </g>
            )
          })}
        </svg>

        {hovered !== null && (
          <div className="tooltip" style={{ left: chartWidth / 2, top: 36 }}>
            <div className="tooltip-title">{weekLabel(hovered.week)}</div>
            <div className="tooltip-value">
              {hovered.total} {noun}
            </div>
          </div>
        )}
      </div>
      <div className="legend-scale">
        {itemKeys.map((key, index) => (
          <span key={key} className="status-chip">
            <span
              className="legend-swatch"
              style={{ background: COLORS[index % COLORS.length] }}
              aria-hidden
            />
            {itemNames.get(key) ?? key}
          </span>
        ))}
      </div>
    </div>
  )
}
